import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@weretradeit/hooks';
import {
  ErrorBoundary,
  FleetChrome,
  GlyphErrorPage,
  GlyphLoader,
  IconSprite,
  Loader,
} from '@weretradeit/ui-react';
import type { FleetSessionUser, NavigationConfig } from '@weretradeit/ui-react';
import { APP_KEY, config } from './config';
import { ladeGlyphSzeneImLeerlauf } from './glyphScene';
import { routes } from './types/routes';

// Code splitting: Lazy-load pages for better performance
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const FeaturedShowcasePage = lazy(() => import('./pages/FeaturedShowcasePage'));
const ShowcaseManagerPage = lazy(() => import('./pages/ShowcaseManagerPage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));

/* `navigation: { sections }` statt der flachen `navItems` aus 0.4.0. Die
   Symbole folgen v3/icons/nav-icons.json (Flotten-Regel H10/H10b); alle fuenf
   Namen stehen im Sprite (names.json). */
const navigation: NavigationConfig = {
  sections: [
    {
      items: [
        { label: 'Dashboard', to: routes.dashboard, icon: 'dashboard' },
        { label: 'Showcase Manager', to: routes.showcases, icon: 'layers' },
        { label: 'Featured Showcase', to: routes.featured, icon: 'featured' },
        { label: 'Analytics', to: routes.analytics, icon: 'analytics' },
        { label: 'Settings', to: routes.settings, icon: 'settings' },
      ],
    },
  ],
};

/* Die Umgebung aus derselben Hostregel, nach der config.ts die API waehlt —
   sie kennt zusaetzlich `staging-*`, das `fleetEnvironment()` nicht kennt.
   Steuert Staging-Streifen, Umgebungsabzeichen und die Launcher-Adressen. */
const fleetEnv = config.environment === 'development' ? 'dev' : config.environment;

/* Der Nutzer, den useAuth() schon geladen hat — FleetChrome fragt `/me` dann
   nicht ein zweites Mal. Abmelden laeuft eingebaut (`fleetLogout`):
   `POST /api/auth/logout`, dann zur Anmeldung mit `?redirect=<origin>` — dieselbe
   Auth-Wurzel, die das Vanilla-Widget in index.html bekam. */
type Auth = ReturnType<typeof useAuth>;
function sessionUser(user: Auth['user'], profile: Auth['profile']): FleetSessionUser | null {
  if (!user && !profile) return null;
  return {
    id: user?.id ?? null,
    email: profile?.email ?? null,
    name: profile?.display_name ?? user?.username ?? null,
    role: profile?.role ?? null,
    avatarUrl: profile?.avatar_url ?? null,
  };
}

function App() {
  const { user, profile, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  /* Die GlyphField-Szene erst nach dem Einhaengen und im Leerlauf — siehe
     glyphScene.ts. Ab hier zeichnet das Feld der Sitzungspruefung. */
  useEffect(() => {
    ladeGlyphSzeneImLeerlauf();
  }, []);

  /* Erster Ladebildschirm, der sonst nichts zu zeigen hat (GLYPHFIELD.md § 3):
     die Szene der App statt des entfallenen LoadingOverlay. Der Text steht als
     role="status" daneben, das Feld ist Kunst. */
  if (loading) {
    return <GlyphLoader appKey={APP_KEY} label="Session wird geprüft…" />;
  }

  return (
    <>
      {/* Der Sprite MUSS im Dokument stehen: Menuesymbole und der
          Launcher-Knopf loesen ueber <use href="#i-…"> auf. Die Datei kopiert
          scripts/copy-design-assets.mjs vor jedem Bau nach public/icons/. */}
      <IconSprite src="/icons/sprite.svg" />
      <FleetChrome
        appKey={APP_KEY}
        navigation={navigation}
        activePath={location.pathname}
        onNavigate={(item) => {
          if (item.to) navigate(item.to);
        }}
        user={sessionUser(user, profile)}
        authBaseUrl="/api/auth"
        env={fleetEnv}
      >
        {/* Eine Seite, die abstuerzt, zeigt die 500 der Flotte INNERHALB der
            Huelle (Kopfzeile bleibt); `key` setzt die Grenze beim
            Seitenwechsel zurueck. Ohne `fallback`: er gewaenne vor `appKey`. */}
        <ErrorBoundary appKey={APP_KEY} key={location.pathname}>
          <Suspense fallback={<Loader label="Seite wird geladen…" />}>
            <Routes>
              <Route path={routes.home} element={<Navigate to={routes.dashboard} replace />} />
              <Route path={routes.dashboard} element={<DashboardPage />} />
              <Route path={routes.featured} element={<FeaturedShowcasePage />} />
              <Route path={routes.showcases} element={<ShowcaseManagerPage />} />
              <Route path={routes.analytics} element={<AnalyticsPage />} />
              <Route path={routes.settings} element={<SettingsPage />} />
              {/* Bis 4.2.1 leitete jede unbekannte Adresse still aufs
                  Dashboard um. Jetzt die 404 der Flotte mit der Szene der App. */}
              <Route path="*" element={<GlyphErrorPage appKey={APP_KEY} homeHref={routes.dashboard} />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </FleetChrome>
    </>
  );
}

export default App;
